// offline-identity.ts — the offline data layer's identity record and the pure
// rules that decide whose on-device data a page may read or write.
//
// Session cookies are HttpOnly and auth.me() swallows network failures, so the
// SDK can't learn "who is signed in" on its own while offline. Instead every
// data-plane (and heartbeat) response states it via the additive
// `X-Backlit-UID` header, and /_backlit/ui states the glow's auth mode plus the
// signed-in user (or `user: null` = signed out). This module folds those
// signals into one persisted IdentityRecord (stored in the mirror's `meta`
// under "identity" by the caller) and answers the questions the read/write
// paths ask of it: is it stale, which partition does a store map to, may this
// response be written through, is this page's own proof still current.
//
// Everything here is PURE apart from the injectable clock: no I/O, no
// globals, no imports beyond the generated permission enum. Callers persist
// the returned record.
//
// Clock: every offline module reads time through now() so tests can drive it
// with _setClockForTest (the heartbeat.ts / fab.ts `_…ForTest` precedent).
// Offline modules must never call Date.now() directly.

import { PERMISSIONS, type Permission } from "./permissions.gen.js";

// STALE_AFTER_MS — a per-user partition is refused (and deleted) once the last
// successful session proof is more than 7 days old. Exactly 7 days is still
// fresh.
export const STALE_AFTER_MS = 604_800_000;

// CLOCK_SKEW_MS — the tolerance for clock disagreement, used twice: a proof's
// verifiedAt may not exceed the server Date by more than this (so a clock set
// forward can't pre-date freshness), and a local clock more than this far
// behind the highest value it has shown before is treated as rolled back.
export const CLOCK_SKEW_MS = 300_000;

// MirrorStoreName is the set of stores the mirror holds. capture values and
// auth.users() are never mirrored.
export type MirrorStoreName = "data" | "userdata" | "records";

// IdentityRecord is the persisted identity. uid is "" when no user is known to
// be signed in. verifiedAt is the (clamped) time of the last accepted proof;
// highWater is the highest LOCAL clock value observed, used only to detect a
// clock rolled back. gen increments on every identity transition so a page can
// tell its own proof has been superseded.
export interface IdentityRecord {
  uid: string;
  permission: Permission | "";
  authMode: string;
  verifiedAt: number;
  highWater: number;
  gen: number;
}

// PageProof is this page's own evidence: the uid and record generation it last
// saw proven by a response it received itself.
export interface PageProof {
  uid: string;
  gen: number;
}

// ProofChange classifies what an accepted proof did to the record.
export type ProofChange = "none" | "first" | "identity_change";

// UIConfigAction classifies what a /_backlit/ui answer did to the record.
export type UIConfigAction = "none" | "authmode_changed" | "signed_out" | "identity_change";

let clock: (() => number) | null = null;

// now returns the current time in ms — the injected test clock when set,
// otherwise Date.now().
export function now(): number {
  return clock ? clock() : Date.now();
}

// _setClockForTest installs a deterministic clock; null restores Date.now.
export function _setClockForTest(fn: (() => number) | null): void {
  clock = fn;
}

// emptyIdentity is the record for "nothing known": no uid, no auth mode, never
// verified.
export function emptyIdentity(): IdentityRecord {
  return { uid: "", permission: "", authMode: "", verifiedAt: 0, highWater: 0, gen: 0 };
}

// normalizeIdentity validates a record read back from storage (another SDK
// build may have written it) and returns a clean copy, or null when it isn't
// a usable IdentityRecord. Unknown permissions decode to "", bad numbers to 0.
export function normalizeIdentity(raw: unknown): IdentityRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.uid !== "string" || typeof r.authMode !== "string") return null;
  const num = (x: unknown) => (typeof x === "number" && Number.isFinite(x) && x > 0 ? x : 0);
  return {
    uid: r.uid,
    permission: PERMISSIONS.has(r.permission as Permission) ? (r.permission as Permission) : "",
    authMode: r.authMode,
    verifiedAt: num(r.verifiedAt),
    highWater: num(r.highWater),
    gen: Number.isInteger(r.gen) ? num(r.gen) : 0,
  };
}

// parseServerDate parses an HTTP `Date` header to epoch ms, or 0 when the
// header is missing or unparseable (0 = "no server time", see clampVerified).
export function parseServerDate(raw: string | null | undefined): number {
  if (!raw) return 0;
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : 0;
}

// clampVerified is the verifiedAt a proof taken at local time `at` earns: the
// local time, but never more than CLOCK_SKEW_MS past the server's Date, so a
// device whose clock is set forward can't extend its own freshness.
function clampVerified(at: number, date: number): number {
  return date > 0 ? Math.min(at, date + CLOCK_SKEW_MS) : at;
}

// applyProof folds one response's identity statement into the record. An
// empty uid carries no information (an anonymous session or an older server)
// and changes nothing. A proof for a different uid than the record's is an
// identity change: gen advances and the permission is forgotten (the caller
// purges the previous uid's partitions and quarantines its queued writes).
export function applyProof(
  rec: IdentityRecord | null,
  proof: { uid: string; date: number },
  at: number,
): { record: IdentityRecord; change: ProofChange; previousUid: string } {
  const base = rec ?? emptyIdentity();
  const previousUid = base.uid;
  if (proof.uid === "") return { record: { ...base }, change: "none", previousUid };
  const record: IdentityRecord = {
    ...base,
    uid: proof.uid,
    verifiedAt: clampVerified(at, proof.date),
    highWater: Math.max(base.highWater, at),
  };
  let change: ProofChange = "none";
  if (previousUid === "") {
    change = "first";
  } else if (previousUid !== proof.uid) {
    change = "identity_change";
    record.gen = base.gen + 1;
    record.permission = "";
  }
  return { record, change, previousUid };
}

// applyUIConfig folds a /_backlit/ui answer into the record:
//   - an auth mode that differs from a known one resets the record (the caller
//     purges the whole mirror), then applies the user proof, if any;
//   - `user: null` while a uid is known means signed out: uid + permission
//     are cleared and gen advances;
//   - otherwise a present user is applied as a proof and sets the permission.
// A non-empty authMode is always recorded.
export function applyUIConfig(
  rec: IdentityRecord | null,
  ui: { authMode: string; user: { uid: string; permission: Permission | "" } | null },
  date: number,
  at: number,
): { record: IdentityRecord; action: UIConfigAction; previousUid: string } {
  const base = rec ?? emptyIdentity();
  const previousUid = base.uid;
  let action: UIConfigAction = "none";
  let record: IdentityRecord = { ...base };
  if (ui.authMode !== "" && base.authMode !== "" && ui.authMode !== base.authMode) {
    action = "authmode_changed";
    record = { ...emptyIdentity(), authMode: ui.authMode, gen: base.gen + 1 };
  }
  if (ui.user === null) {
    if (action === "none" && record.uid !== "") {
      action = "signed_out";
      record = { ...record, uid: "", permission: "", gen: record.gen + 1 };
    }
  } else if (ui.user.uid !== "") {
    const res = applyProof(record, { uid: ui.user.uid, date }, at);
    record = { ...res.record, permission: ui.user.permission };
    if (action === "none" && res.change === "identity_change") action = "identity_change";
  }
  if (ui.authMode !== "") record.authMode = ui.authMode;
  return { record, action, previousUid };
}

// isStale reports whether the record's proof is too old to trust for a per-user
// partition: never verified, verified more than STALE_AFTER_MS ago, or the
// local clock is more than CLOCK_SKEW_MS behind its own high-water mark (the
// clock was rolled back, so elapsed time can't be trusted).
export function isStale(rec: IdentityRecord, at: number): boolean {
  return (
    rec.verifiedAt === 0 ||
    at - rec.verifiedAt > STALE_AFTER_MS ||
    at < rec.highWater - CLOCK_SKEW_MS
  );
}

// partitionFor maps a store to the mirror partition the current identity may
// use, or null when there is none:
//   - Private: everything is per-user, `u:<uid>`;
//   - Public / Accounts: glow-shared `data` is `g` (anonymous visitors read it
//     too); userdata/records are per-user `u:<uid>`;
//   - an unknown or empty auth mode: nothing.
export function partitionFor(store: MirrorStoreName, rec: IdentityRecord | null): string | null {
  if (!rec) return null;
  const user = rec.uid !== "" ? "u:" + rec.uid : null;
  switch (rec.authMode) {
    case "Private":
      return user;
    case "Public":
    case "Accounts":
      return store === "data" ? "g" : user;
    default:
      return null;
  }
}

// isUserPartition reports whether a partition is per-user (subject to the
// staleness bound and to purges on sign-out / identity change).
export function isUserPartition(p: string): boolean {
  return p.startsWith("u:");
}

// writeThroughAllowed gates mirror write-through: the glow-shared partition
// accepts any 2xx, but a per-user partition only accepts a response whose own
// X-Backlit-UID names that user — so a response can never land in another
// user's partition, even if another tab switched users unnoticed.
export function writeThroughAllowed(partition: string, responseUid: string): boolean {
  return partition === "g" || (responseUid !== "" && partition === "u:" + responseUid);
}

// pageProofValid reports whether this page's own proof still matches the
// persisted record (same signed-in uid, same generation).
export function pageProofValid(
  pp: PageProof | null | undefined,
  rec: IdentityRecord | null | undefined,
): boolean {
  return !!pp && !!rec && rec.uid !== "" && pp.uid === rec.uid && pp.gen === rec.gen;
}

// bumpGen returns a copy of the record with its generation advanced, which
// invalidates every page proof taken against the old generation.
export function bumpGen(rec: IdentityRecord): IdentityRecord {
  return { ...rec, gen: rec.gen + 1 };
}
